import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed, watch } from 'vue'

import { getSessionsByIds, SESSION_QUERY_KEY, SESSIONS_BATCH_QUERY_KEY } from '@/features/sessions/api'
import queryClient from '@/lib/vue-query'
import { DEFAULT_QUERY_STALE_TIME } from '@/lib/vue-query'

const useSessionsBatch = (sessionIdsRef: Ref<string[]>) => {
  const sortedIdsRef = computed(() => [...sessionIdsRef.value].sort())
  const isEnabledRef = computed(() => sortedIdsRef.value.length > 0)

  const query = useQuery({
    queryKey: [SESSIONS_BATCH_QUERY_KEY, { ids: sortedIdsRef }],
    queryFn: () => getSessionsByIds(sortedIdsRef.value),
    enabled: () => isEnabledRef.value,
    staleTime: DEFAULT_QUERY_STALE_TIME,
  })

  watch(
    query.data,
    (result) => {
      if (!result?.sessions) return
      for (const session of result.sessions) {
        if (!session?.id) continue
        queryClient.setQueryData([SESSION_QUERY_KEY, { id: session.id }], { session })
      }
    },
    { immediate: true },
  )

  const isReadyRef = computed(() => {
    if (sortedIdsRef.value.length === 0) return true
    return !!query.data.value
  })

  return {
    isPendingRef: query.isPending,
    isReadyRef,
  }
}

export default useSessionsBatch
